import type { UserSettings } from '@/stores/settingsStore';
import axios, { AxiosError } from 'axios';
import { authService } from './authService';

// Einstellungstypen definieren
export enum EinstellungTyp {
  BenutzerEinstellungen = 1
}

// Interface für die API-Antwort
interface EinstellungResponse {
  typ: number;
  data: string;
}

class SettingsService {
  private baseURL = '/api/v1';

  /**
   * Lädt die Einstellungen eines bestimmten Typs
   */
  async getSettings(typ: EinstellungTyp): Promise<UserSettings | null> {
    // Prüfen, ob ein Token existiert und kein Redirect-Loop aktiv ist
    if (!authService.hasToken() || sessionStorage.getItem('redirected_to_login') === 'true') {
      console.log('Einstellungen-Abruf blockiert: Kein Token oder Redirect-Loop aktiv');
      return null; // Keine API-Anfrage senden, wenn kein Token existiert oder wir im Redirect-Loop sind
    }

    try {
      const response = await axios.get(`${this.baseURL}/einstellungen/${typ}`);
      return JSON.parse(response.data.data) as UserSettings;
    } catch (error: unknown) {
      // 404 bedeutet, dass noch keine Einstellungen existieren
      if (error instanceof AxiosError && error.response && error.response.status === 404) {
        return null;
      }
      console.error('Fehler beim Abrufen der Einstellungen:', error);
      // Bei 401 keine Weiterleitung auslösen
      return null;
    }
  }

  /**
   * Speichert die Einstellungen eines bestimmten Typs
   */
  async saveSettings(typ: EinstellungTyp, settings: UserSettings): Promise<void> {
    // Prüfen, ob ein Token existiert und kein Redirect-Loop aktiv ist
    if (!authService.hasToken() || sessionStorage.getItem('redirected_to_login') === 'true') {
      console.log('Einstellungen-Speichern blockiert: Kein Token oder Redirect-Loop aktiv');
      return; // Keine API-Anfrage senden, wenn kein Token existiert oder wir im Redirect-Loop sind
    }

    const payload: EinstellungResponse = {
      typ: typ,
      data: JSON.stringify(settings)
    };

    try {
      await axios.put(`${this.baseURL}/einstellungen`, payload);
    } catch (error) {
      console.error('Fehler beim Speichern der Einstellungen:', error);
      throw error;
    }
  }

  /**
   * Löscht die Einstellungen eines bestimmten Typs
   */
  async deleteSettings(typ: EinstellungTyp): Promise<void> {
    await axios.delete(`${this.baseURL}/einstellungen/${typ}`);
  }
}

export const settingsService = new SettingsService();
